import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { useEventDetails } from './useEventDetails';
import EventDetails from './EventDetails/EventDetails';
import EventTeams from './EventTeams';
import EventHeader from './EventHeader';
import VoteEvent from './VoteEvent/VoteEvent';
import { Tabs, Dialog, GenericError, PageLoader } from '../common';
import EventBoard from './EventBoard/EventBoard';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import EventJoinRequests from './EventRequests/EventJoinRequests';
import { EVENT_STATUSES } from '../../utils/types';
import Eventinvitations from './EventRequests/EventInvitations';
import { setPageTitle } from '../../utils/helpers';

import './eventPage.css';

const EventPage = () => {
    const auth = useAppSelector(state => state.auth);
    const { eventId } = useParams();
    const navigate = useLocalizedNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        setPageTitle(t('seo.eventDetails.title'));
    }, [t]);

    const {
        isLoading,
        isEventLoading,
        eventDetails,
        error,
        joinEvent,
        leaveEvent,
        changeTeam,
        requestToJoin,
        hasUserJoined,
        foundUserInTeam,
        cancelEvent,
        refreshEventData,
    } = useEventDetails(eventId);

    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showLeaveDialog, setShowLeaveDialog] = useState(false);
    const [selectedTab, setSelectedTab] = useState(t('eventDetails.firstTab'));
    const isOpen = eventDetails?.status === EVENT_STATUSES.OPEN;

    useEffect(() => {
        setSelectedTab(t('eventDetails.firstTab'));
    }, [auth.authData]);

    const sections = useMemo(
        () => [
            t('eventDetails.firstTab'),
            t('eventDetails.secondTab'),
            ...(isOpen ? [t('eventDetails.thirdTab')] : []),
            ...(eventDetails?.isPrivate && isOpen
                ? [t('eventDetails.fourthTab')]
                : []),
        ],
        [auth.authData]
    );

    if (isEventLoading) {
        return <PageLoader />;
    }

    if (error) {
        return <GenericError error={error} />;
    }

    if (!eventDetails) {
        return null;
    }

    const joinEventHandler = (teamId: string) => {
        joinEvent(teamId);
    };

    function changeTeamHandler(userId: string) {
        changeTeam(userId);
    }

    const onRequestAction = (changeTab?: boolean) => {
        refreshEventData();
        if (changeTab) {
            setSelectedTab('Details');
        }
    };

    const isOwner = eventDetails.owner.id === auth.authData?.id;
    const showVoteTools =
        eventDetails.status === EVENT_STATUSES.VOTE_PHASE &&
        hasUserJoined &&
        !foundUserInTeam?.hasVoted &&
        !eventDetails.reports.includes(auth.authData?.id!);

    const showJoinEventButton = eventDetails.isPrivate
        ? eventDetails.isPrivate &&
          (eventDetails.owner.id === auth.authData?.id ||
              eventDetails.invitations.find(
                  inv => inv.user.id === auth.authData?.id
              )!!)
        : true;

    const isEventCancelled =
        eventDetails.status === EVENT_STATUSES.CANCELLED ||
        eventDetails.status === EVENT_STATUSES.CANCELLED_BY_SYSTEM;

    const onShowCancelDialog = () => {
        setShowCancelDialog(true);
    };

    const onShowLeaveDialog = () => {
        setShowLeaveDialog(true);
    };

    const onCancelEvent = () => {
        cancelEvent();
        setShowCancelDialog(false);
    };

    const onLeaveEvent = () => {
        leaveEvent();
        setShowLeaveDialog(false);
    };

    const onChangeTab = (tab: string) => {
        setSelectedTab(tab);
    };

    const onGoToEditPage = () => {
        navigate(`/events/${eventDetails.id}/edit`);
    };

    function onUserReport() {
        refreshEventData();
    }

    return (
        <div className="event-page">
            <EventHeader
                eventDetails={eventDetails}
                hasUserJoined={hasUserJoined}
                onShowCancelDialog={onShowCancelDialog}
                onLeaveEvent={onShowLeaveDialog}
                onRequestToJoin={requestToJoin}
                onGoToEditPage={onGoToEditPage}
                refreshEventData={refreshEventData}
            />

            <main
                className={`event-page-main ${
                    isEventCancelled ? 'disabled' : ''
                }`}
            >
                {(eventDetails.status === EVENT_STATUSES.OPEN ||
                    eventDetails.status === EVENT_STATUSES.VOTE_PHASE) && (
                    <Tabs
                        sections={sections}
                        selectedTab={selectedTab}
                        onChangeTab={onChangeTab}
                        dotIndex={eventDetails.joinRequests.length > 0 ? 3 : -1}
                    />
                )}
                <div className="event-page-content">
                    <EventDetails eventDetails={eventDetails} />
                    {selectedTab === t('eventDetails.firstTab') && (
                        <>
                            {showVoteTools ? (
                                <VoteEvent
                                    eventDetails={eventDetails}
                                    refreshEventData={refreshEventData}
                                    isLoading={isLoading}
                                    foundUserInTeam={foundUserInTeam}
                                    onUserReport={onUserReport}
                                />
                            ) : (
                                <EventTeams
                                    eventDetails={eventDetails}
                                    onJoinEvent={joinEventHandler}
                                    onChangeTeam={changeTeamHandler}
                                    showJoinEventButton={
                                        showJoinEventButton &&
                                        eventDetails.status !==
                                            EVENT_STATUSES.CANCELLED &&
                                        eventDetails.status !==
                                            EVENT_STATUSES.CANCELLED_BY_SYSTEM
                                    }
                                    isOwner={isOwner}
                                    refreshEventData={refreshEventData}
                                    foundUserInTeam={foundUserInTeam}
                                />
                            )}
                        </>
                    )}
                    {selectedTab === t('eventDetails.secondTab') && (
                        <EventBoard isEventCancelled={isEventCancelled} />
                    )}
                    {selectedTab === t('eventDetails.thirdTab') && (
                        <Eventinvitations
                            invitations={eventDetails.invitations}
                        />
                    )}
                    {selectedTab === t('eventDetails.fourthTab') && (
                        <EventJoinRequests
                            eventDetails={eventDetails}
                            onRequestAction={onRequestAction}
                            isOwner={isOwner}
                        />
                    )}
                </div>
            </main>
            <Dialog
                isOpen={showCancelDialog}
                title={t('eventDetails.cancelDialogTitle')}
                bodyText={t('eventDetails.cancelDialogBody')}
                onClose={() => setShowCancelDialog(false)}
                onConfirm={onCancelEvent}
                isWarning
            />
            <Dialog
                isOpen={showLeaveDialog}
                title={t('eventDetails.leaveDialogTitle')}
                bodyText={t('eventDetails.leaveDialogBody')}
                onClose={() => setShowLeaveDialog(false)}
                onConfirm={onLeaveEvent}
                isWarning
            />
        </div>
    );
};

export default EventPage;
